import { useMemo, useState } from "react";
import { DateTime } from "luxon";
import { ScorePage } from "src/pages/authenticated/ScoreTemplates";
import {
  Asterisk,
  Breakpoints,
  Button,
  Card,
  CheckboxInput,
  Colors,
  fontMedium,
  fontSmall,
  formatDate,
  Icon,
  TimeInput,
  useModal,
} from "swing-components";

import { AlertError, ContentSingleColumn, Toggle } from "~components";
import { translate } from "~utils";

import { RequestDetails } from "../../TimesheetEntry";
import {
  BreakPeriod,
  calculateTotalHours,
  extractBreakPeriodsFromPunches,
  type NoMealBreakReason,
  type TimeCard,
} from "../../utils";
import {
  NonCompliantMealBreakActionSheet,
  type MealBreakOption,
} from "../NonCompliantMealBreakActionSheet";
import {
  getTimeCardEntriesDefaultTimes,
  getTimeCardEntriesEndTimeIndex,
  getTimeCardEntriesStartTimeIndex,
  getTimeCardMealBreakIndices,
} from "./utils";

/***** Types *****/
type TimeCardEditProps = {
  timeCard: TimeCard;
  /** @default false */
  error?: boolean;
  isLoading?: boolean;
  userState?: string;
  onSubmit: (formData: TimeCard, complianceReason?: NoMealBreakReason) => void;
};

/**
 * View | TimeCardEdit
 *
 * View component for editing timecard entries with form validation and submission
 */
export function TimeCardEditView(props: TimeCardEditProps) {
  const { timeCard, error = false, isLoading, userState, onSubmit } = props;

  /*** Form State ***/
  const [form, setForm] = useState<TimeCard & { isCertified: boolean }>({
    ...timeCard,
    isCertified: false, // Default to false
  });

  // State for tracking selected meal break compliance reason
  const [selectedNoMealBreakReason, setSelectedNoMealBreakReason] = useState<
    MealBreakOption | undefined
  >();

  /*** Modal Setup ***/
  const { openModal, closeModal } = useModal({
    component: (
      <NonCompliantMealBreakActionSheet
        onDismiss={() => {
          setSelectedNoMealBreakReason(undefined);
          closeModal();
        }}
        onSubmit={() => {
          if (selectedNoMealBreakReason === "confirmed-break") {
            // If break needs to be recorded, just close modal and return to edit view
            setSelectedNoMealBreakReason(undefined);
          } else {
            // For other cases, submit the form with compliance reason
            const tempForm = { ...form };
            delete (tempForm as Partial<typeof form>).isCertified;
            onSubmit(tempForm as TimeCard, selectedNoMealBreakReason as NoMealBreakReason);
          }
          closeModal();
        }}
        isSubmissionPending={isLoading}
        onOptionChange={(option) => setSelectedNoMealBreakReason(option)}
      />
    ),
    modalOptions: {
      cssClass: "action-sheet-modal",
      breakpoints: [0, 1],
      initialBreakpoint: 1,
    },
  });

  /*** Constants ***/
  const startTimeIndex = useMemo(() => {
    return getTimeCardEntriesStartTimeIndex(form.entries);
  }, [form.entries]);

  const endTimeIndex = useMemo(() => {
    return getTimeCardEntriesEndTimeIndex(form.entries);
  }, [form.entries]);

  const { startTimeIndex: mealBreak1StartTimeIndex, endTimeIndex: mealBreak1EndTimeIndex } =
    getTimeCardMealBreakIndices({
      timeCard: form,
      mealBreakIndex: 1,
    });

  const hasMealBreak1 =
    mealBreak1StartTimeIndex !== undefined || mealBreak1EndTimeIndex !== undefined;

  /**
   * Determines if CA meal break compliance warning should be shown.
   * Uses available context to simplify the logic.
   */
  const shouldShowCaMealBreakWarning = useMemo(() => {
    // Only show warning for CA users
    if (userState !== "CA") return false;

    // Only show warning for requests > 5 hours
    const { startTime, endTime } = timeCard.request;
    if (!startTime || !endTime) return false;

    const requestDurationHours = endTime.diff(startTime, "hours").hours;
    if (requestDurationHours <= 5) return false;

    // Calculate meal break duration using existing extractBreakPeriodsFromPunches
    const { breakPeriods } = extractBreakPeriodsFromPunches(form.entries);
    const totalBreakMinutes = breakPeriods.reduce((sum: number, breakPeriod: BreakPeriod) => {
      if (breakPeriod.start && breakPeriod.end) {
        return sum + breakPeriod.end.diff(breakPeriod.start, "minutes").minutes;
      }
      return sum;
    }, 0);

    console.log("totalBreakMinutes", totalBreakMinutes);

    // Show warning if meal break < 30 minutes
    return totalBreakMinutes < 30;
  }, [userState, timeCard.request, form.entries]);

  // The form is valid when all entries have a timestamp and the certified
  // checkbox is toggled.
  const isFormValid = form.entries.every((entry) => entry.ts) && form.isCertified;
  const pageTitle = formatDate({ date: timeCard.date, format: "Wed, Jan 1" });

  // Get default times for initial values
  const { defaultStartTime, defaultEndTime, defaultMealBreak1StartTime, defaultMealBreak1EndTime } =
    getTimeCardEntriesDefaultTimes({ timeCard, now: DateTime.now() });

  /*** Handlers ***/
  /**
   * Handles form submission by calling the parent handler with form data
   */
  function handleSubmit() {
    // Check if we need to show compliance validation modal
    if (shouldShowCaMealBreakWarning) {
      openModal();
      return;
    }

    // Direct submission for compliant cases
    const tempForm = { ...form };
    delete (tempForm as Partial<typeof form>).isCertified;
    onSubmit(tempForm as TimeCard, undefined);
  }

  /**
   * Handles toggling meal break on/off
   * @param isSelected - Whether the meal break toggle is selected/enabled
   */
  function handleMealBreakToggle(isSelected: boolean) {
    // When toggling off
    if (isSelected === false) {
      if (mealBreak1StartTimeIndex === undefined || mealBreak1EndTimeIndex === undefined) return;

      // Remove the meal break 1 start and end time using the index
      setForm((prevForm) => ({
        ...prevForm,
        entries: prevForm.entries.filter(
          (_, index) => index !== mealBreak1StartTimeIndex && index !== mealBreak1EndTimeIndex,
        ),
      }));
      return;
    }
    // When toggling on
    // Add the meal break 1 start time and end time using the
    // original TimeCard, defaulting to undefined if not found
    const { startTimeIndex, endTimeIndex } = getTimeCardMealBreakIndices({
      timeCard, // Using the original TimeCard, not the form
      mealBreakIndex: 1,
    });
    const mealBreakStartTimeEntry = startTimeIndex
      ? timeCard.entries[startTimeIndex]
      : {
          id: "",
          type: "PUNCH_OUT" as const,
          ts: undefined,
        };
    const mealBreakEndTimeEntry = endTimeIndex
      ? timeCard.entries[endTimeIndex]
      : {
          id: "",
          type: "PUNCH_IN" as const,
          ts: undefined,
        };

    // Add the meal break 1 start and end time using the index
    setForm((prevForm) => {
      const updateEntries = [
        prevForm.entries[0],
        mealBreakStartTimeEntry, // We know the this should be at this index
        mealBreakEndTimeEntry, // We know that this should be at this index
        ...prevForm.entries.slice(1), // Everything else flows after
      ];

      return {
        ...prevForm,
        entries: updateEntries,
      };
    });
  }

  /*** Render ***/
  return (
    <ScorePage title={pageTitle} isLoading={isLoading} hasBack>
      <ContentSingleColumn>
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: 24,
            padding: 16,
            [Breakpoints.mediumDeviceAndUp]: { padding: 0 },
          }}
        >
          {error && (
            <AlertError
              // TODO: This looks odd
              margin="0px"
            />
          )}

          {/* School & Request Details */}
          <RequestDetails
            schoolName={timeCard.school.name}
            date={timeCard.date}
            startTime={timeCard.request.startTime}
            endTime={timeCard.request.endTime}
          />

          {/* Form */}
          <Card>
            <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Start & End Time Section*/}
              <div>
                {/* Title */}
                <div css={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Icon name="Clock" />
                  <p css={{ margin: 0, ...fontMedium({ fontWeight: 600 }) }}>
                    {translate("TIMECARD_ENTRY_FORM_TIME_LABEL")}
                    <Asterisk />
                  </p>
                </div>

                {/* Start Time */}
                <div
                  css={{
                    padding: "16px 0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <p css={{ margin: 0 }}>{translate("TIMECARD_ENTRY_FORM_START_TIME")}</p>
                  <TimeInput
                    value={form.entries?.[startTimeIndex ?? -1]?.ts}
                    initialValue={defaultStartTime}
                    max={form.entries?.[endTimeIndex ?? -1]?.ts}
                    onChange={(newStartTime) => {
                      setForm((prevForm) => {
                        // Handle creating a new entry
                        if (startTimeIndex === undefined) {
                          return {
                            ...prevForm,
                            entries: [
                              {
                                id: "",
                                type: "PUNCH_IN" as const,
                                ts: newStartTime,
                              },
                              ...prevForm.entries,
                            ],
                          };
                        }

                        // Handle editing an entry
                        const updatedEntries = [...prevForm.entries];
                        updatedEntries[startTimeIndex] = {
                          ...updatedEntries[startTimeIndex],
                          ts: newStartTime,
                        };
                        return {
                          ...prevForm,
                          entries: updatedEntries,
                        };
                      });
                    }}
                  />
                </div>
                {/* End Time */}
                <div
                  css={{
                    padding: "16px 0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderTop: `1px solid ${Colors.slate200}`,
                  }}
                >
                  <p css={{ margin: 0 }}>{translate("TIMECARD_ENTRY_FORM_END_TIME")}</p>
                  <TimeInput
                    value={form.entries?.[endTimeIndex ?? -1]?.ts}
                    initialValue={defaultEndTime}
                    min={form.entries?.[startTimeIndex ?? -1]?.ts}
                    onChange={(newEndTime) => {
                      setForm((prevForm) => {
                        // Handle creating a new entry
                        if (endTimeIndex === undefined) {
                          return {
                            ...prevForm,
                            entries: [
                              ...prevForm.entries,
                              {
                                id: "",
                                type: "PUNCH_OUT" as const,
                                ts: newEndTime,
                              },
                            ],
                          };
                        }

                        // Handle editing an entry
                        const updatedEntries = [...prevForm.entries];
                        updatedEntries[endTimeIndex] = {
                          ...updatedEntries[endTimeIndex],
                          ts: newEndTime,
                        };
                        return {
                          ...prevForm,
                          entries: updatedEntries,
                        };
                      });
                    }}
                  />
                </div>
              </div>

              <Divider />

              {/* Meal Break #1 */}
              <div>
                {/* Title */}
                <div
                  css={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div css={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Icon name="Food" />
                    <p css={{ margin: 0, ...fontMedium({ fontWeight: 600 }) }}>
                      {translate("TIMECARD_ENTRY_FORM_MEAL_BREAK_LABEL", {
                        count: 1,
                      })}
                    </p>
                  </div>
                  <Toggle isSelected={hasMealBreak1} onToggleChange={handleMealBreakToggle} />
                </div>

                {/* Meal Break #1 Start Time */}
                <div
                  css={{
                    padding: "16px 0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <p css={{ margin: 0, color: !hasMealBreak1 ? Colors.slate400 : undefined }}>
                    {translate("TIMECARD_ENTRY_FORM_MEAL_BREAK_IN")}
                  </p>
                  <TimeInput
                    isDisabled={!hasMealBreak1}
                    value={form.entries?.[mealBreak1StartTimeIndex ?? -1]?.ts}
                    initialValue={defaultMealBreak1StartTime}
                    max={form.entries?.[mealBreak1EndTimeIndex ?? -1]?.ts}
                    onChange={(newMealBreak1StartTime) => {
                      setForm((prevForm) => {
                        // Handle creating a new entry
                        if (mealBreak1StartTimeIndex === undefined) {
                          return {
                            ...prevForm,
                            entries: [
                              prevForm.entries[0],
                              {
                                id: "",
                                type: "PUNCH_OUT" as const,
                                ts: newMealBreak1StartTime,
                              },
                              ...prevForm.entries.slice(1),
                            ],
                          };
                        }

                        // Handle editing an entry
                        const updatedEntries = [...prevForm.entries];
                        updatedEntries[mealBreak1StartTimeIndex] = {
                          ...updatedEntries[mealBreak1StartTimeIndex],
                          ts: newMealBreak1StartTime,
                        };
                        return {
                          ...prevForm,
                          entries: updatedEntries,
                        };
                      });
                    }}
                  />
                </div>

                {/* Meal Break #1 End Time */}
                <div
                  css={{
                    padding: "16px 0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderTop: `1px solid ${Colors.slate200}`,
                  }}
                >
                  <p css={{ margin: 0, color: !hasMealBreak1 ? Colors.slate400 : undefined }}>
                    {translate("TIMECARD_ENTRY_FORM_MEAL_BREAK_OUT")}
                  </p>
                  <TimeInput
                    isDisabled={!hasMealBreak1}
                    value={form.entries?.[mealBreak1EndTimeIndex ?? -1]?.ts}
                    initialValue={defaultMealBreak1EndTime}
                    min={form.entries?.[mealBreak1StartTimeIndex ?? -1]?.ts}
                    onChange={(newMealBreak1EndTime) => {
                      setForm((prevForm) => {
                        // Handle creating a new entry
                        if (mealBreak1EndTimeIndex === undefined) {
                          return {
                            ...prevForm,
                            entries: [
                              ...prevForm.entries.slice(0, 2),
                              {
                                id: "",
                                type: "PUNCH_OUT" as const,
                                ts: newMealBreak1EndTime,
                              },
                              ...prevForm.entries.slice(2),
                            ],
                          };
                        }

                        // Handle editing an entry
                        const updatedEntries = [...prevForm.entries];
                        updatedEntries[mealBreak1EndTimeIndex] = {
                          ...updatedEntries[mealBreak1EndTimeIndex],
                          ts: newMealBreak1EndTime,
                        };
                        return {
                          ...prevForm,
                          entries: updatedEntries,
                        };
                      });
                    }}
                  />
                </div>
              </div>

              {/* CA Meal Break Compliance Warning */}
              {shouldShowCaMealBreakWarning && hasMealBreak1 && (
                <div css={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <Icon name="Alert" color={Colors.yellow600} />
                  <p css={{ ...fontSmall(), margin: 0 }}>
                    {translate("TIMECARD_ENTRY_FORM_MEAL_BREAK_HINT_REMINDER")}
                  </p>
                </div>
              )}

              <Divider />

              {/* Total Hours */}
              <div
                css={{
                  display: "flex",
                  justifyContent: "space-between",
                  ...fontMedium({ fontWeight: 600 }),
                }}
              >
                <span>{translate("LABEL_TOTAL_HOURS")}</span>
                <span>{calculateTotalHours([form])}</span>
              </div>
            </div>
          </Card>

          {/* Certification Checkbox */}
          <Card>
            <div css={{ marginLeft: -16, marginRight: -16 }}>
              <CheckboxInput
                label={translate("TIMECARD_ENTRY_CERTIFICATION_CHECKBOX_LABEL")}
                isChecked={form.isCertified}
                onCheckedChange={(isCertified) => setForm((prev) => ({ ...prev, isCertified }))}
              />
            </div>
          </Card>

          {/* Submit Button */}
          <div
            css={{
              display: "flex",
              justifyContent: "center",
              [Breakpoints.mediumDeviceAndUp]: {
                justifyContent: "flex-end",
              },
            }}
          >
            <Button
              type="primary"
              onClick={handleSubmit}
              isFluid={!matchMedia(Breakpoints.mediumDeviceAndUp.replace("@media ", "")).matches}
              status={!isFormValid ? "disabled" : "enabled"}
            >
              {translate("TIMESHEET_SUBMIT_ENTRY_LABEL")}
            </Button>
          </div>
        </div>
      </ContentSingleColumn>
    </ScorePage>
  );
}

/***** Helper Functions *****/
function Divider() {
  return <div css={{ width: "100%", height: 1, backgroundColor: Colors.slate300 }} />;
}
